import { DndContext, DragOverlay, useSensor, useSensors } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { ComplianceWarningDialog } from '~components/CompliancePanel/ComplianceWarningDialog';
import {
  HEADER_PANEL_Z_INDEX,
  LoadingSpinner,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  HEADER_HEIGHT,
  JOB_COLUMN_WIDTH,
} from '~pages/Dispatch/components/drivers/constants';
import {
  DriverSchedulerFiltersBar,
  SearchBar,
} from '~pages/Dispatch/components/drivers/DriverSchedulerFiltersBar';
import { JobCard, JobPlacement } from '~pages/Dispatch/components/drivers/JobCard';
import { TimeSlotCellGrid } from '~pages/Dispatch/components/drivers/TimeSlotCellGrid';
import { UnassignedColumn } from '~pages/Dispatch/components/drivers/UnassignedColumn';
import OrderForm from '~pages/Dispatch/components/OrderForm';
import { useDriverScheduler } from '~pages/Dispatch/hooks/useDriverScheduler';
import { useStores } from '~store';
import NoDnDDataAttributeMouseSensor from '~utils/dnd-kit/NoDnDDataAttributeMouseSensor';
import NoDnDDataAttributePointerSensor from '~utils/dnd-kit/NoDnDDataAttributePointerSensor';
import rectIntersectionInPointerRow from '~utils/dnd-kit/rectIntersectionInPointerRow';
import { hexToRgba } from '~utils/utilFunctions';

import { IDispatchViewComponent } from '../IDispatchViewComponent';
import SharedDatesFilter from '../SharedDatesFilter';
import DriversViewEffects from './DriversViewEffects';
import AssignedJobsFilters from './Filters/AssignedJobsFilters';
import { JobDialogsContextProvider } from './JobDialogsContext';

interface DriversViewProps extends IDispatchViewComponent {}

const DriversView = ({ viewSelector }: DriversViewProps) => {
  const theme = useTheme();
  const { driverSchedulerStore } = useStores();
  const {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    pendingSchedulerAssignment,
    isComplianceOverrideSubmitting,
    confirmSchedulerAssignment,
    cancelSchedulerAssignment,
  } = useDriverScheduler();
  const isLoading =
    // While the Orders sub-tab owns the eager load it shows its own in-column
    // spinner, so the per-page fetches must not flicker the blocking overlay.
    (driverSchedulerStore.isLoadingUnassignedJobs &&
      !driverSchedulerStore.isOrdersViewActive) ||
    driverSchedulerStore.isLoadingAssignees ||
    driverSchedulerStore.isLoadingAssignedJobs;

  const [isNewOrderDialogOpen, setIsNewOrderDialogOpen] = useState(false);
  const [isOrderFormDrawerMinimized, setIsOrderFormDrawerMinimized] = useState(false);

  const dndSensors = useSensors(
    useSensor(NoDnDDataAttributeMouseSensor, {
      activationConstraint: { delay: 50, tolerance: 0 },
    }),
    useSensor(NoDnDDataAttributePointerSensor, { activationConstraint: { distance: 1 } }),
  );

  return (
    <>
      <DriversViewEffects />
      <JobDialogsContextProvider>
        <DndContext
          collisionDetection={rectIntersectionInPointerRow}
          sensors={dndSensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box sx={{ gridRow: 'span 3', position: 'relative' }}>
            {isLoading && (
              <Box
                sx={{
                  bgcolor: hexToRgba(theme.brandV2.colors.treadGray8, 0.5),
                  inset: 0,
                  position: 'absolute',
                  zIndex: HEADER_PANEL_Z_INDEX + 4,
                }}
              />
            )}

            <UnassignedColumn />
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            {viewSelector}

            <Box>
              <DriverSchedulerFiltersBar
                isOrderFormDrawerMinimized={isOrderFormDrawerMinimized}
                onNewOrderClick={() => {
                  if (isOrderFormDrawerMinimized) {
                    setIsOrderFormDrawerMinimized(false);
                  } else {
                    setIsNewOrderDialogOpen(true);
                  }
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: 'background.paper',
              borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              px: 2,
              py: 1,
            }}
          >
            <SharedDatesFilter
              dateRangePickerMode="icon"
              dateRangeUseTodayFormat
              view="drivers"
            />
          </Box>

          <Box>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: `calc(${HEADER_HEIGHT}px + 94px)`,
                  left: JOB_COLUMN_WIDTH,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: HEADER_PANEL_Z_INDEX + 4,
                  height: '100%',
                  bgcolor: hexToRgba(theme.brandV2.colors.treadGray8, 0.5),
                }}
              >
                <LoadingSpinner isVisible={true} />
              </Box>
            )}

            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                display: 'flex',
                gap: 1,
                px: 2,
                py: 1,
              }}
            >
              <SearchBar isAlwaysExpanded />

              {!driverSchedulerStore.assignedJobsFilters.search && (
                <AssignedJobsFilters />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    height: `calc(100vh - ${HEADER_HEIGHT}px - 97px - var(--tread-agent-dock-height, 0px))`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <TimeSlotCellGrid />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <DragOverlay>
            {driverSchedulerStore.draggedJob ? (
              <>
                <JobCard
                  jobId={driverSchedulerStore.draggedJob.id}
                  jobPlacement={JobPlacement.DRAGGING}
                />
              </>
            ) : null}
          </DragOverlay>
        </DndContext>

        <OrderForm
          defaultDate={driverSchedulerStore.dateFilters.startDate}
          isMinimized={isOrderFormDrawerMinimized}
          isOpen={isNewOrderDialogOpen}
          onClose={() => {
            setIsOrderFormDrawerMinimized(false);
            setIsNewOrderDialogOpen(false);
          }}
          onMinimize={() => setIsOrderFormDrawerMinimized(true)}
        />
      </JobDialogsContextProvider>
      {pendingSchedulerAssignment && (
        <ComplianceWarningDialog
          isOpen={true}
          result={pendingSchedulerAssignment.complianceResult}
          isSubmitting={isComplianceOverrideSubmitting}
          onCancel={cancelSchedulerAssignment}
          onOverride={confirmSchedulerAssignment}
        />
      )}
    </>
  );
};

export default observer(DriversView);
